import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import { resolveImageURL } from 'volto-essvoltocomponents/helpers';
import { PageHeaderBackground } from '../../Molecules/PageHeaderBackground';
import { ContentWide, ContentWrapper, PageSection } from '../../Layout';
import Pagination from '../../Molecules/Pagination/Pagination';
import { ResponsiveGrid } from '../../Atoms/ResponsiveGrid';
import Carousel from '../../Atoms/Carousel';
import ResponsiveComponentSwitch, {
  useWindowWidth,
} from '../../Atoms/ResponsiveComponentSwitch';
import TeaserCardVertical from '../../Molecules/TeaserCard/TeaserCardVertical';
import './DisseminationPage.scss';

const PAGE_SIZE = 12;
const MOBILE_PAGE_SIZE = 6;
const SUBREQUEST = 'disseminationItems';
const RESOURCE_LISTS_SUBREQUEST = 'disseminationResourceLists';

const GRID_BREAKPOINTS = [
  { minWidth: 0, columns: 1 },
  { minWidth: 768, columns: 2 },
  { minWidth: 1024, columns: 3 },
  { minWidth: 1440, columns: 4 },
];

const messages = defineMessages({
  loading: {
    id: 'dissemination.loading',
    defaultMessage: 'Loading\u2026',
  },
  error: {
    id: 'dissemination.error',
    defaultMessage: 'There was an error loading the content. Please try again.',
  },
  empty: {
    id: 'dissemination.empty',
    defaultMessage: 'No items found.',
  },
});

const DisseminationPage = ({ content: contentProp }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const sectionRef = useRef(null);

  // Use prop when available (SmartFolderView), fall back to Redux (addon route)
  const reduxContent = useSelector((state) => state.content?.data);
  const content = contentProp || reduxContent;

  const [currentPage, setCurrentPage] = useState(1);
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const contentPath = content?.['@id'] ? flattenToAppURL(content['@id']) : '/';
  const windowWidth = useWindowWidth();
  const isCarouselLayout = windowWidth < 768;
  const itemsPerPage = isCarouselLayout ? MOBILE_PAGE_SIZE : PAGE_SIZE;

  // Lightweight catalog of the folder. @id and @type are enough to know every
  // resource_list path, so nested resources can be excluded across pages.
  const resourceListsSubrequestKey = `${RESOURCE_LISTS_SUBREQUEST}-${contentPath}-${itemsPerPage}`;


  const catalogItems = useSelector(
    (state) =>
      state.search.subrequests?.[resourceListsSubrequestKey]?.items || [],
  );

  const catalogLoaded = useSelector(
    (state) =>
      state.search.subrequests?.[resourceListsSubrequestKey]?.loaded || false,
  );

  useEffect(() => {
    if (contentPath && !catalogLoaded) {
      dispatch(
        searchContent(
          contentPath,
          {
            portal_type: ['resource', 'resource_list', 'success_story'],
            review_state: 'published',
            b_size: 10000,
          },
          resourceListsSubrequestKey,
        ),
      );
    }
  }, [dispatch, contentPath, resourceListsSubrequestKey, catalogLoaded]);

  // Full set of resource_list @ids across the whole dissemination folder
  // (not just the current page). Used to exclude nested resources robustly.
  const allResourceListPaths = useMemo(() => {
    return new Set(
      catalogItems
        .filter((item) => item['@type'] === 'resource_list')
        .map((item) => item['@id']),
    );
  }, [catalogItems]);

  const itemsSubrequestKey = `${SUBREQUEST}-${contentPath}-${currentPage}-${itemsPerPage}`;

  const items = useSelector(
    (state) => state.search.subrequests?.[itemsSubrequestKey]?.items || [],
  );
  const total = useSelector(
    (state) => state.search.subrequests?.[itemsSubrequestKey]?.total || 0,
  );
  const loading = useSelector(
    (state) => state.search.subrequests?.[itemsSubrequestKey]?.loading ?? false,
  );
  const loaded = useSelector(
    (state) => state.search.subrequests?.[itemsSubrequestKey]?.loaded ?? false,
  );
  const error = useSelector(
    (state) => state.search.subrequests?.[itemsSubrequestKey]?.error || null,
  );

  useEffect(() => {
    if (!contentPath || loaded) return;

    dispatch(
      searchContent(
        contentPath,
        {
          portal_type: ['resource', 'resource_list', 'success_story'],
          'path.depth': 1,
          review_state: 'published',
          sort_on: 'effective',
          sort_order: 'reverse',
          b_size: itemsPerPage,
          b_start: (currentPage - 1) * itemsPerPage,
          metadata_fields: ['_all'],
        },
        itemsSubrequestKey,
      ),
    );
  }, [dispatch, contentPath, currentPage, itemsPerPage, itemsSubrequestKey, loaded]);

  // ── Exclude resources that live inside a resource_list ───────────────────
  // Uses the full set of resource_list paths, so a nested resource is excluded
  // even when its parent resource_list lives on a different page.
  const excludeNestedResources = useCallback(
    (itemList) => {
      return itemList.filter((item) => {
        if (item['@type'] !== 'resource') return true;
        for (const path of allResourceListPaths) {
          if (item['@id'].startsWith(path + '/')) return false;
        }
        return true;
      });
    },
    [allResourceListPaths],
  );

  // ── Unified display items & pagination ─────────────────────────────
  const displayItems = useMemo(
    () => excludeNestedResources(items),
    [items, excludeNestedResources],
  );

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));

  const scrollToSection = () => {
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  };

  const heroImageUrl = content
    ? resolveImageURL(content, '/@@images/hero_image/hero')
    : '';

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, []);

  //card elements 
  const rangeTotal = total;
  const rangeStart = displayItems.length
    ? (currentPage - 1) * itemsPerPage + 1
    : 0;
  const rangeEnd = rangeStart + displayItems.length - 1;

  const showNextCarouselPage = () => {
    setCarouselStartIndex(0);
    setCurrentPage((page) => page + 1);
  };

  const showPrevCarouselPage = () => {
    setCarouselStartIndex(MOBILE_PAGE_SIZE - 1);
    setCurrentPage((page) => page - 1);
  };

  const cardElements = displayItems.map((item) => {
    const imageUrl = resolveImageURL(item, '/@@images/thumbnail_image/preview');
    return (
      <TeaserCardVertical
        key={item['@id']}
        title={item.title || ''}
        description={
          item.short_description?.data || item.description?.data || ''
        }
        imageUrl={imageUrl}
        url={item['@id'] || ''}
        caption={formatDate(item.effective)}
      />
    );
  });

  return (
    <div className="dissemination-page">
      <PageHeaderBackground
        title={content?.title || ''}
        details=""
        description={
          content.short_description?.data || content.description?.data || ''
        }
        backgroundImage={heroImageUrl}
      />

      <PageSection paddingBottom="var(--ess-space-7xl)" ref={sectionRef}>
        <ContentWrapper
          className="dissemination-page__listing-section"
          minHeight="55vh"
        >
          <ContentWide alignment="center">
            {error ? (
              <p className="dissemination-page__error" role="alert">
                {intl.formatMessage(messages.error)}
              </p>
            ) : loading || !loaded ? (
              <p className="dissemination-page__loading" aria-live="polite">
                {intl.formatMessage(messages.loading)}
              </p>
            ) : displayItems.length === 0 ? (
              <p className="dissemination-page__empty">
                {intl.formatMessage(messages.empty)}
              </p>
            ) : (
              <>
                <div className="dissemination-page__listing">
                  <ResponsiveComponentSwitch
                    breakpoints={[
                      {
                        minWidth: 768,
                        component: (
                          <>
                            <ResponsiveGrid
                              breakpoints={GRID_BREAKPOINTS}
                              gap="1.5rem"
                            >
                              {cardElements}
                            </ResponsiveGrid>
                            {totalPages > 1 && (
                              <div className="dissemination-page__pagination">
                                <Pagination
                                  currentPage={currentPage}
                                  totalPages={totalPages}
                                  onPageChange={(page) => {
                                    setCurrentPage(page);
                                    scrollToSection();
                                  }}
                                />
                              </div>
                            )}
                          </>
                        ),
                      },
                    ]}
                    defaultComponent={
                      <>
                        <Carousel
                          key={`${currentPage}-${carouselStartIndex}`}
                          pageKey={`${currentPage}-${carouselStartIndex}`}
                          startIndex={carouselStartIndex}
                          onEdgeNext={
                            currentPage < totalPages ? showNextCarouselPage : undefined
                          }
                          onEdgePrev={currentPage > 1 ? showPrevCarouselPage : undefined}
                          range={{
                            start: rangeStart,
                            end: rangeEnd,
                            total: rangeTotal,
                          }}
                        >
                          {cardElements}
                        </Carousel>
                      </>
                    }  
                  />
                </div>
              </>
            )}
          </ContentWide>
        </ContentWrapper>
      </PageSection>
    </div>
  );
};

export default DisseminationPage;
